<template lang="pug">
#message-register-confirm
  // title
  .d-flex.flex-row.justify-content-center.mb-2
    .page-title 新規送信

  // progress
  ProcessIndicator(v-model="indicator")
  .mb-3.separator

  // message detail
  MessageRegisterDetail.mb-4(v-model="messageDetail" :showTempFile="showTempFile")

  // buttons
  .d-flex.flex-row.justify-content-center
    b-button.btn.send-btn.mr-2(@click="onSendMessage") 送信
    b-button.btn.back-btn(@click="onCancel") 戻る

  //- file upload error modal
  FileUploadErrorModal(v-model="showUploadErrorModal")

  // loading component
  Loading(:isBusy="isBusy", :message="loadingMessage")
  ProgressLoading(v-model="currentUploadCount", :max="uploadCount", :loading="progressLoading", message="ファイルアップロード")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { Getter } from "vuex-class";
import { userStore, mobileMessageRegisterStore } from "@/store/store";
import { MobileMessageRegisterState } from "@/store/MobileMessageRegister";
import { MobileUrl } from "@/commons/constants/MobileUrl";

import SendMessagesService, { SendMessagesToFamiliesPostModel, SendMessagesToOtherUserGroupsPostModel, SendMessageType } from "@/commons/services/SendMessages";
import SendOtherUserMailMessagesService from "@/commons/services/SendOtherUserMailMessages";
import ExistS3FilesService, { ExistS3FilesPostModel } from "@/commons/services/ExistS3Files";
import { S3LocationRequest, S3LocationResponse, S3LocationType, S3LocationMessage } from "@/commons/models/S3Location";
import S3UploadsService from "@/commons/services/S3Uploads";
import { SendMessageTargetOtherUserModalModel } from "@/mobile/components/modals/SendMessageTargetOtherUserModal.vue";
import {
  SendMessageTargetMessageGroupModalModel,
  SendMessageTargetMessageGroupModel,
  SendMessageTargetOtherUserGroupModel,
  SendMessageTargetResidentGroupModel,
  TargetType
} from "@/mobile/components/modals/SendMessageTargetMessageGroupModal.vue";
import { SenderType } from "@/mobile/pages/sendMessages/MessageRegisterInput.vue";
import { SenderType as FamilySenderType } from "@/commons/models/Resident";
import FamiliesService, { FamilyGetModel } from "@/commons/services/Families";
import OtherUserGroupMembersService from "@/commons/services/OtherUserGroupMembers";
import SendMessageGroupMembersService, {
  SendMessageGroupMembersFamilyGetModel,
  SendMessageGroupMembersOtherUserGroupMemberGetModel
} from "@/commons/services/SendMessageGroupMembers";
import S3LocationsService from "@/commons/services/S3Locations";
import { GroupType } from "@/commons/services/SendMessageGroups";
import { PlanType } from "@/commons/services/Plans";
import SystemError from "@/commons/models/SystemError";

import FileUploadErrorModal from "@/mobile/basics/FileUploadErrorModal.vue";
import Loading from "@/mobile/basics/Loading.vue";
import ProgressLoading from "@/mobile/basics/ProgressLoading.vue";
import MessageRegisterDetail, { MessageRegisterDetailModel } from "@/mobile/components/MessageRegisterDetail.vue";
import ProcessIndicator, { ProcessIndicatorModel } from "@/mobile/basics/ProcessIndicator.vue";
import { SendMessageTargetFamilyModalModel } from "@/mobile/components/modals/SendMessageTargetFamilyModal.vue";
import OtherUserGroup from "@/commons/models/OtherUserGroup";

/**
 * ### 新規送信確認コンポーネント
 */
@Component({
  components: {
    FileUploadErrorModal,
    Loading,
    ProgressLoading,
    MessageRegisterDetail,
    ProcessIndicator
  }
})
export default class MessageRegisterConfirm extends Vue {
  /** 家族サービス */
  @Inject("FamiliesService") familiesService!: FamiliesService;
  /** 施設関係者グループメンバー取得サービス */
  @Inject("OtherUserGroupMembersService") otherUserGroupMembersService!: OtherUserGroupMembersService;
  /** 宛先グループメンバーサービス */
  @Inject("SendMessageGroupMembersService") sendMessageGroupMembersService!: SendMessageGroupMembersService;
  /** メッセージ送信サービス */
  @Inject("SendMessagesService") sendMessagesService!: SendMessagesService;
  /** メール送信サービス(施設関係者) */
  @Inject("SendOtherUserMailMessagesService") sendOtherUserMailMessagesService!: SendOtherUserMailMessagesService;
  /** S3格納先URLサービス */
  @Inject("S3LocationsService") s3LocationsService!: S3LocationsService;
  /** S3ファイルアップロードサービス */
  @Inject("S3UploadsService") s3UploadsService!: S3UploadsService;
  /** S3ファイル存在確認サービス */
  @Inject("ExistS3FilesService") existS3FilesService!: ExistS3FilesService;

  /** ### 新規送信入力画面での入力情報 */
  @Getter("mobileMessageRegisterState") state!: MobileMessageRegisterState;

  /** インジケータ */
  indicator: Array<ProcessIndicatorModel> = [
    { text: "入力", current: false },
    { text: "確認", current: true },
    { text: "完了", current: false }
  ];

  /** メッセージ詳細 */
  messageDetail: MessageRegisterDetailModel | any = {};
  /** 添付ファイルを表示するか否か */
  showTempFile: boolean = false;

  /** 読み込み中モーダル表示可否 */
  isBusy: boolean = false;
  /** 読み込み中モーダルメッセージ */
  loadingMessage: string = "読込み中";

  /** 現在のアップロード数 */
  currentUploadCount: number = 0;
  /** アップロード数最大値 */
  uploadCount: number = 0;
  /** プログレス付きローディング表示可否 */
  progressLoading: boolean = false;

  /** ファイルアップロードエラーモーダル表示可否 */
  showUploadErrorModal: boolean = false;

  /**
   * ### vue lifecycle created
   */
  created(): void {
    const planType: PlanType = userStore.facility.planType;
    if (PlanType.LIGHT <= planType) {
      this.showTempFile = true;
    } else {
      this.showTempFile = false;
    }

    return;
  }

  /**
   * ### vue lifecycle mounted
   */
  async mounted(): Promise<void> {
    // ブラウザバック対応。メッセージ送信後にブラウザバックされた場合にstoreに内容がないため表示する内容が無い
    // storeがリセットされていたら入力画面へ遷移させる
    if (this.state.exist == false) {
      this.$router.push(MobileUrl.MessageRegisterInput);
    }

    await this.createView();
    return;
  }

  /**
   * ### 画面表示情報を作成する
   */
  private async createView(): Promise<void> {
    this.isBusy = true;

    const messageDetail: MessageRegisterDetailModel = this.createMessageDetail(this.state);
    this.messageDetail = messageDetail;

    this.isBusy = false;

    return;
  }

  /**
   * ### 送信メッセージ表示コンポーネントに渡す情報を作成する
   */
  private createMessageDetail(state: MobileMessageRegisterState): MessageRegisterDetailModel {
    console.log("state.isSharedMessages:", state.isSharedMessages);

    const messageDetail: MessageRegisterDetailModel = {
      receivers: this.createReceivers(state),
      isSharedMessages: state.isSharedMessages,
      title: state.title,
      text: state.text,
      addReadButton: state.addReadButton,
      addOptionText: state.addOptionText,
      addReplyButton: state.addReplyButton,
      files: state.files
    };

    return messageDetail;
  }

  /**
   * ### 宛先を作成する
   */
  private createReceivers(state: MobileMessageRegisterState): string {
    switch (state.type) {
      // 家族全員
      case SenderType.ALL_FAMILY: {
        return "家族全員";
      }
      // 家族個別
      case SenderType.FAMILY: {
        let targets: string = "";
        for (let targetResident of state.targets as Array<SendMessageTargetFamilyModalModel>) {
          for (let targetFamily of targetResident.targets) {
            if (targetFamily.isTarget) {
              // 詳細タブの宛先は家族の場合は（部屋名・利用者名）をつけるように仕様変更になったのでここで判定する
              let receiverName: string =
                targetFamily.room != null && targetFamily.name != null ? `${targetFamily.familyName}（${targetFamily.room}・${targetFamily.name}）` : targetFamily.familyName;
              if (targets == "") {
                targets = receiverName;
              } else {
                targets = targets + "｜" + receiverName;
              }
            }
          }
        }
        return targets;
      }
      // 施設関係者個別
      case SenderType.OTHER_USER: {
        let targets: string = "";
        for (let group of state.targets as Array<SendMessageTargetOtherUserModalModel>) {
          for (let target of group.targets) {
            if (target.isTarget) {
              if (targets == "") {
                targets = target.memberName;
              } else {
                targets = targets + "｜" + target.memberName;
              }
            }
          }
        }
        return targets;
      }
      // 宛先グループ
      case SenderType.GROUP: {
        let targets: string = "";
        for (const target of state.targets as Array<SendMessageTargetMessageGroupModalModel>) {
          for (let group of target.targets) {
            if (group.isTarget) {
              if (targets == "") {
                targets = group.name;
              } else {
                targets = targets + "｜" + group.name;
              }
            }
          }
        }
        return targets;
      }
      default:
        return "";
    }
  }

  /**
   * ### 送信ボタン押下イベント
   *
   * - メッセージを送信する
   */
  async onSendMessage(): Promise<void> {
    this.isBusy = true;

    const sendMessageParam: { sendMessage: SendMessagesToFamiliesPostModel | SendMessagesToOtherUserGroupsPostModel; sendTarget: string } = await this.createParams();

    // ファイルアップロードするタイミングでファイルが削除（移動含む）、リネームされた場合は失敗するのでここでtry-catchする
    // アップロードに失敗した場合は添付されたファイルを全てリセットしてモーダルで再添付を促す
    let files: { fileUrls: string[]; fileNames: string[] } = { fileUrls: [], fileNames: [] };
    try {
      files = await this.uploadFiles();
    } catch (error) {
      this.isBusy = false;

      this.showUploadErrorModal = true;
      this.state.files = [];

      return;
    }

    // S3のアップロードは時間がかかるので他画面へ遷移されてしまうことがありえる
    // 画面遷移していたら処理は中断する
    if (this.$route.path != MobileUrl.MessageRegisterConfirm) {
      return;
    }

    this.isBusy = true;
    this.loadingMessage = "送信中";

    sendMessageParam.sendMessage.fileUrls = files.fileUrls;
    sendMessageParam.sendMessage.fileNames = files.fileNames;

    // 種別で呼び出すAPIを切り替える
    if (sendMessageParam.sendTarget == "family") {
      await this.sendMessagesService.postToFamilies(userStore, sendMessageParam.sendMessage as SendMessagesToFamiliesPostModel);
    } else if (sendMessageParam.sendTarget == "otherUser") {
      await this.sendMessagesService.postToOtherUser(userStore, sendMessageParam.sendMessage as SendMessagesToOtherUserGroupsPostModel);
    }

    this.isBusy = false;

    // 画面遷移していたら処理は中断する
    if (this.$route.path != MobileUrl.MessageRegisterConfirm) {
      mobileMessageRegisterStore.resetMobileMessageRegisterState();
      return;
    }

    // 新規送信完了画面へ遷移する
    mobileMessageRegisterStore.resetMobileMessageRegisterState();
    this.$router.push(MobileUrl.MessageRegisterComplete);

    return;
  }

  /**
   * ### メッセージ送信用のパラメータを作成する
   */
  private async createParams(): Promise<{ sendMessage: SendMessagesToFamiliesPostModel | SendMessagesToOtherUserGroupsPostModel; sendTarget: string }> {
    switch (this.state.type) {
      // 家族全員
      case SenderType.ALL_FAMILY: {
        return await this.createAllFamilySendMessageParams();
      }
      // 家族個別
      case SenderType.FAMILY: {
        return this.createFamilySendMessageParams();
      }
      // 施設関係者個別
      case SenderType.OTHER_USER: {
        return await this.createOtherUserSendMessageParams();
      }
      // グループ
      case SenderType.GROUP: {
        return await this.createMessageGroupSendMessageParams();
      }
      // その他の場合は空を返す
      default: {
        return { sendMessage: undefined as any, sendTarget: "" };
      }
    }
  }

  /**
   * ### 「家族全員」のメッセージ送信用パラメータを作成する
   */
  private async createAllFamilySendMessageParams(): Promise<{
    sendMessage: SendMessagesToFamiliesPostModel | SendMessagesToOtherUserGroupsPostModel;
    sendTarget: string;
  }> {
    const allFamily: Array<FamilyGetModel> = await this.getNonmaskedFamilies();

    const lineSortKeys: Array<string> = [];
    const mailSortKeys: Array<string> = [];
    const targetNames: string[] = [];

    for (const targetFamily of allFamily) {
      if (targetFamily.senderType == FamilySenderType.LINE && targetFamily.familyLineId != undefined) {
        lineSortKeys.push(targetFamily.residentsSortKey);
      } else if (targetFamily.senderType == FamilySenderType.MAIL && targetFamily.familyMailaddress != undefined) {
        mailSortKeys.push(targetFamily.residentsSortKey);
      }
    }

    targetNames.push("家族全員");

    const params: { sendMessage: SendMessagesToFamiliesPostModel | SendMessagesToOtherUserGroupsPostModel } = this.createSendMessageParams(
      lineSortKeys,
      mailSortKeys,
      targetNames.join(),
      `${this.state.type}`
    );

    return { sendMessage: params.sendMessage, sendTarget: "family" };
  }

  /**
   * ### 「家族全員」で送信する対象の家族を取得する
   */
  private async getNonmaskedFamilies(): Promise<Array<FamilyGetModel>> {
    let response: Array<FamilyGetModel> = [];

    try {
      const params: any = {
        masking: "none"
      };
      response = await this.familiesService.getsInFacility(userStore, params);
    } catch (error) {
      const systemerror: SystemError = error as SystemError;
      // 413エラーの場合、全件取得した結果がペイロード制限に引っかかったことになるので、index指定で段階的に取得する
      if (systemerror.statusCode == 413) {
        const count: number = await this.familiesService.getCountInFacility(userStore);
        const loopEnd: number = Math.ceil(count / 300);

        for (let i = 0; i < loopEnd; i++) {
          const params: any = {
            startIndex: i * 300,
            endIndex: i * 300 + 300,
            masking: "none"
          };
          response = response.concat(await this.familiesService.getsInFacility(userStore, params));
        }
      }
    }
    return response;
  }

  /**
   * ### 「家族個別」のメッセージ送信用パラメータを作成する
   */
  private createFamilySendMessageParams(): {
    sendMessage: SendMessagesToFamiliesPostModel | SendMessagesToOtherUserGroupsPostModel;
    sendTarget: string;
  } {
    const lineSortKeys: Array<string> = [];
    const mailSortKeys: Array<string> = [];
    const targetNames: string[] = [];

    for (const targetResident of this.state.targets as Array<SendMessageTargetFamilyModalModel>) {
      for (const targetFamily of targetResident.targets) {
        if (targetFamily.isTarget) {
          targetNames.push(targetFamily.familyName);

          if (targetFamily.senderType == FamilySenderType.LINE && targetFamily.familyLineId != undefined) {
            lineSortKeys.push(targetFamily.residentsSortKey);
          } else if (targetFamily.senderType == FamilySenderType.MAIL && targetFamily.familyMailaddress != undefined) {
            mailSortKeys.push(targetFamily.residentsSortKey);
          }
        }
      }
    }

    const params: { sendMessage: SendMessagesToFamiliesPostModel | SendMessagesToOtherUserGroupsPostModel } = this.createSendMessageParams(
      lineSortKeys,
      mailSortKeys,
      targetNames.join("、"),
      `${this.state.type}`
    );

    return { sendMessage: params.sendMessage, sendTarget: "family" };
  }

  /**
   * ### 「施設関係者個別」のメッセージ送信用パラメータを作成する
   */
  private createOtherUserSendMessageParams(): {
    sendMessage: SendMessagesToFamiliesPostModel | SendMessagesToOtherUserGroupsPostModel;
    sendTarget: string;
  } {
    const lineSortKeys: Array<string> = [];
    const mailSortKeys: Array<string> = [];
    const targetNames: string[] = [];

    for (const group of this.state.targets as Array<SendMessageTargetOtherUserModalModel>) {
      for (const target of group.targets) {
        if (target.isTarget) {
          targetNames.push(target.memberName);

          if (target.senderType == FamilySenderType.LINE && target.memberLineId != undefined) {
            lineSortKeys.push(target.otherUserGroupsSortKey);
          } else if (target.senderType == FamilySenderType.MAIL && target.memberMailaddress != undefined) {
            mailSortKeys.push(target.otherUserGroupsSortKey);
          }
        }
      }
    }

    const params: { sendMessage: SendMessagesToFamiliesPostModel | SendMessagesToOtherUserGroupsPostModel } = this.createSendMessageParams(
      lineSortKeys,
      mailSortKeys,
      targetNames.join("、"),
      `${this.state.type}`
    );

    return { sendMessage: params.sendMessage, sendTarget: "otherUser" };
  }

  /**
   * ### 「グループ」のメッセージ送信用パラメータを作成する
   */
  private async createMessageGroupSendMessageParams(): Promise<{
    sendMessage: SendMessagesToFamiliesPostModel | SendMessagesToOtherUserGroupsPostModel;
    sendTarget: string;
  }> {
    const lineSortKeys: Array<string> = [];
    const mailSortKeys: Array<string> = [];
    const targetNames: string[] = [];
    let messageType: string = "";
    let sendTarget: string = "";

    for (const target of this.state.targets as Array<SendMessageTargetMessageGroupModalModel>) {
      switch (target.type) {
        // 宛先グループ
        case TargetType.SEND_MESSAGE_GROUP_FOR_FAMILY:
        case TargetType.SEND_MESSAGE_GROUP_FOR_OTHER_USER: {
          const targets: Array<SendMessageTargetMessageGroupModel> = target.targets as Array<SendMessageTargetMessageGroupModel>;
          for (const group of targets) {
            if (group.isTarget) {
              // 家族への送信
              if (group.type == GroupType.FAMILY) {
                const members: Array<SendMessageGroupMembersFamilyGetModel> = await this.getSendMessageGroupFamilyMembers(group.id);

                // 送信用のLINEIDまたはメールアドレスを追加していく
                for (const member of members) {
                  if (member.senderType == FamilySenderType.LINE && member.familyLineId) {
                    lineSortKeys.push(member.residentsSortKey as string);
                  } else if (member.senderType == FamilySenderType.MAIL && member.familyMailaddress) {
                    mailSortKeys.push(member.residentsSortKey as string);
                  }
                }
                targetNames.push(group.name);
                // 家族に送信する
                sendTarget = "family";
              }
              // 施設関係者への送信
              else if (group.type == GroupType.OTHER_USER) {
                const members: Array<SendMessageGroupMembersOtherUserGroupMemberGetModel> = await this.getSendMessageGroupOtherGroupUserMembers(group.id);

                // 送信用のLINEIDまたはメールアドレスを追加していく
                for (const member of members) {
                  if (member.senderType == FamilySenderType.LINE && member.memberLineId) {
                    lineSortKeys.push(member.otherUserGroupsSortKey as string);
                  } else if (member.senderType == FamilySenderType.MAIL && member.memberMailaddress) {
                    mailSortKeys.push(member.otherUserGroupsSortKey as string);
                  }
                }
                targetNames.push(group.name);
                // 家族に送信する
                sendTarget = "otherUser";
              }
              // 施設関係者に送信するので種別を3にする
              messageType = "3";
            }
          }
          break;
        }
        // 利用者グループ
        case TargetType.RESIDENT_GROUP: {
          // 全家族を取得して利用者グループにひもづく家族をMAPにする
          const allFamily: Array<FamilyGetModel> = await this.getNonmaskedFamilies();
          const residentGroupMemberMap = new Map<number, Array<FamilyGetModel>>();
          for (const family of allFamily) {
            const families: Array<FamilyGetModel> | undefined = residentGroupMemberMap.get(family.groupId);
            if (families) {
              families.push(family);
              residentGroupMemberMap.set(family.groupId, families);
            } else {
              residentGroupMemberMap.set(family.groupId, [family]);
            }
          }

          const targets: Array<SendMessageTargetResidentGroupModel> = target.targets as Array<SendMessageTargetResidentGroupModel>;
          for (const group of targets) {
            if (group.isTarget) {
              const members: Array<FamilyGetModel> | undefined = residentGroupMemberMap.get(group.id);
              if (members) {
                // 送信用のLINEIDまたはメールアドレスを追加していく
                for (const member of members) {
                  if (member.senderType == FamilySenderType.LINE && member.familyLineId) {
                    lineSortKeys.push(member.residentsSortKey as string);
                  } else if (member.senderType == FamilySenderType.MAIL && member.familyMailaddress) {
                    mailSortKeys.push(member.residentsSortKey as string);
                  }
                }
                targetNames.push(group.name);

                // 利用者グループに送信するので種別を6にする
                messageType = "6";
                // 家族に送信する
                sendTarget = "family";
              }
            }
          }
          break;
        }
        // 施設関係者グループ
        case TargetType.OTHER_USER_GROUP: {
          const targets: Array<SendMessageTargetOtherUserGroupModel> = target.targets as Array<SendMessageTargetOtherUserGroupModel>;
          for (const group of targets) {
            if (group.isTarget) {
              const members = await this.getOtherUserGroupMembers(group.id);
              for (const member of members) {
                if (member.senderType == FamilySenderType.LINE && member.memberLineId) {
                  lineSortKeys.push(member.otherUserGroupsSortKey as string);
                } else if (member.senderType == FamilySenderType.MAIL && member.memberMailaddress) {
                  mailSortKeys.push(member.otherUserGroupsSortKey as string);
                }
              }
              targetNames.push(group.name);

              // 施設関係者グループに送信するので種別を7にする
              messageType = "7";
              // 家族に送信する
              sendTarget = "otherUser";
            }
          }
          break;
        }
      }
    }

    const params: { sendMessage: SendMessagesToFamiliesPostModel | SendMessagesToOtherUserGroupsPostModel } = this.createSendMessageParams(
      lineSortKeys,
      mailSortKeys,
      targetNames.join("、"),
      messageType
    );

    return { sendMessage: params.sendMessage, sendTarget };
  }

  /**
   * ### 宛先グループ(家族)のメンバーを取得する
   */
  private async getSendMessageGroupFamilyMembers(groupId: number): Promise<Array<SendMessageGroupMembersFamilyGetModel>> {
    const members: Array<SendMessageGroupMembersFamilyGetModel> = (await this.sendMessageGroupMembersService.get(
      userStore,
      groupId
    )) as Array<SendMessageGroupMembersFamilyGetModel>;
    return members;
  }

  /**
   * ### 宛先グループ(施設関係者)のメンバーを取得する
   */
  private async getSendMessageGroupOtherGroupUserMembers(groupId: number): Promise<Array<SendMessageGroupMembersOtherUserGroupMemberGetModel>> {
    const members: Array<SendMessageGroupMembersOtherUserGroupMemberGetModel> = (await this.sendMessageGroupMembersService.get(
      userStore,
      groupId
    )) as Array<SendMessageGroupMembersOtherUserGroupMemberGetModel>;
    return members;
  }

  /**
   * ### 施設関係者グループにひもづく施設関係者グループメンバーを取得する
   */
  private async getOtherUserGroupMembers(grouppId: number): Promise<Array<OtherUserGroup>> {
    const response: Array<OtherUserGroup> = await this.otherUserGroupMembersService.get(userStore, grouppId);
    return response;
  }

  /**
   * ### 送信用情報からメッセージ送信用パラメータを作成する
   *
   * - LINE送信パラメータを作成する、送信先が無い場合はundefinedを格納する
   * - メール送信パラメータを作成する、送信先が無い場合はundefinedを格納する
   */
  private createSendMessageParams(
    lineSortKeys: Array<string>,
    mailSortKeys: Array<string>,
    targetName: string,
    messageType: string
  ): { sendMessage: SendMessagesToFamiliesPostModel | SendMessagesToOtherUserGroupsPostModel } {
    const sendLineMessage: SendMessagesToFamiliesPostModel = {
      targetName: targetName,

      sortKeysForLine: lineSortKeys,
      sortKeysForMail: mailSortKeys,

      isSharedMessages: this.state.isSharedMessages,

      title: this.$stringUtil.convertYen(this.state.title),
      message: this.$stringUtil.convertYen(this.state.text),
      addOptionText: this.state.addOptionText,
      addReadButton: this.state.addReadButton,
      addReplyButton: this.state.addReplyButton,

      writerId: userStore.loginUser.userId,
      writtenBy: userStore.loginUser.name,

      fileUrls: [],
      fileNames: [],
      type: messageType as SendMessageType
    };

    return { sendMessage: sendLineMessage };
  }

  /**
   * ### ファイルをアップロードする
   *
   * - アップロードしたファイル情報を返却する
   */
  private async uploadFiles(): Promise<{ fileUrls: string[]; fileNames: string[] }> {
    let fileUrls: string[] = [];
    let fileNames: string[] = [];

    try {
      if (0 < this.state.files.length) {
        const urls: Array<S3LocationResponse> = await this.createUploadUrls(this.state.files);

        // プログレス付きモーダルを表示する
        // 進捗はファイルのアプロード状況を反映させるようにする
        this.currentUploadCount = 0;
        this.uploadCount = this.state.files.length;
        this.progressLoading = true;

        // アップロード
        for (let i = 0; i < urls.length; i++) {
          this.currentUploadCount = i;
          await this.s3UploadsService.uploadFile(urls[i], this.state.files[i]);

          // DBにはファイルではなく、添付ファイルのS3URLを保持するためURL配列を作成する
          const preUrl: string = urls[i].url.endsWith("/") ? urls[i].url : `${urls[i].url}/`;
          const s3Url: string = `${preUrl}${urls[i].fields["key"]}`;
          fileUrls.push(s3Url);
        }
        // アップロードしたファイルはサムネイル画像が作成されたり、movからmp4への変換が行われる
        // ファイルの存在確認をして変換されたか確認する
        await this.existFile(fileUrls);
      }

      for (const file of this.state.files) {
        fileNames.push(file.name);
      }
    } finally {
      this.progressLoading = false;
    }

    return { fileUrls: fileUrls, fileNames: fileNames };
  }

  /**
   * ### ファイルアップロード先のURLを発行する
   */
  private async createUploadUrls(files: Array<File>): Promise<Array<S3LocationResponse>> {
    let extensions: Array<string> = [];
    for (const file of files) {
      const _file: Array<string> = file.name.split("/");
      const _extensions: Array<string> = _file[_file.length - 1].split(".");
      extensions.push("." + _extensions[_extensions.length - 1]);
    }
    const params: S3LocationMessage = {
      userId: userStore.loginUser.userId
    };
    const requestBody: S3LocationRequest = {
      type: S3LocationType.MESSAGE,
      count: files.length,
      extensions: extensions,
      params: params,
      acl: "public-read"
    };
    const url: Array<S3LocationResponse> = await this.s3LocationsService.post(userStore, requestBody);

    return url;
  }

  /**
   * ### ファイルの存在確認を行う
   */
  private async existFile(fileUrls: Array<string>): Promise<void> {
    // ファイルがアップロードされているかをチェックする
    const fileRequestBody: ExistS3FilesPostModel = { urls: fileUrls };
    await this.existS3FilesService.post(userStore, fileRequestBody);

    // サムネイルのファイルが存在しているかをチェックする
    let thumbnailUrls: Array<string> = [];
    for (const url of fileUrls) {
      // サムネイルのファイル名は<元ファイル名>_thumbnailになる
      const splitedUrl: string[] = url.split("/");
      const splitedFileName: string[] = splitedUrl[splitedUrl.length - 1].split(".");

      // mov、mp4以外の場合はサムネイルをメッセージ送信時に作成するのでスキップ
      if (splitedFileName[1] != "mov" && splitedFileName[1] != "mp4") {
        continue;
      }

      const thumbnailFileName: string = splitedFileName[0] + "_thumbnail.jpeg";
      let thunmbnailUrl: string = "";
      for (let i = 0; i < splitedUrl.length - 1; i++) {
        thunmbnailUrl += splitedUrl[i] + "/";
      }
      thunmbnailUrl += thumbnailFileName;
      thumbnailUrls.push(thunmbnailUrl);
    }

    if (0 < thumbnailUrls.length) {
      const thumbnailRequestBody: ExistS3FilesPostModel = { urls: thumbnailUrls };
      await this.existS3FilesService.post(userStore, thumbnailRequestBody);
    }

    return;
  }

  /**
   * ### キャンセルボタン押下イベント
   *
   * - 新規送信入力画面へ遷移する
   */
  onCancel(): void {
    this.$router.push(MobileUrl.MessageRegisterInput);
    return;
  }
}
</script>

<style lang="scss" scopeed>
@import "../../../styles/mobile/_colors.scss";

#message-register-confirm {
  #text {
    white-space: pre-wrap;
    word-wrap: break-word;
  }
  .preview-image,
  .preview-video {
    padding: 0.25rem;
    border: 1px solid $border-default;
    border-radius: 0.25rem;
  }
  .send-btn,
  .back-btn {
    min-width: 84px;
  }
}
</style>
